import {Fragment, ReactElement, useState} from "react"
import {isBoolean, pick} from "lodash-es"
import {TBoatInfo} from "@ui/api/user"
import {t} from "@ui/common/intl"
import {useStore} from "@ui/stores"
import EditIcon from "@ui/icons/common/Edit"
import RightIcon from "@ui/icons/common/Right"
import PlusIcon from "@ui/icons/common/Plus"
import CaptainIcon from "@ui/icons/profile/Captain"
import LogoutIcon from "@ui/icons/profile/Logout"
import BoatIcon from "@ui/icons/profile/Boat"
import BoatCommonIcon from "@ui/icons/profile/BoatCommon"
import BoatDimensionsIcon from "@ui/icons/profile/BoatDimensions"
import BoatTrackingIcon from "@ui/icons/profile/BoatTracking"
import SwipePanel, {SwipePanelHeight} from "@ui/components/services/common/SwipePanel"
import {ProfilePage, TProfilePageProps} from "./common"
import ConfirmationModal from "./common/modal/Confirmation"

const Main = ({close, setPage, isShow}: TProfilePageProps): ReactElement => {
  const {profile: {info, hasBoatInfo}, user, dictionary: {boatTypes, boatClasses}} = useStore()
  const [confirmLogout, setConfirmLogout] = useState(false)

  const constructInfo = (obj: Partial<TBoatInfo>) => {
    const parseEntry = ([key, value]: [key: string, value: string | number | boolean | null]) => {
      const k = t(`profile.boat.${key}.title`)
      const v = isBoolean(value)
        ? t(`profile.boat.boolean.${value}.title`)
        : key === "boat_type"
          ? boatTypes.find(({code}) => code === value)?.desc
          : key === "boat_class"
            ? boatClasses.find(({code}) => code === value)?.desc
            : value
      return (
        <Fragment key={`boat-info-${key}`}>
          {value === undefined || value === null
            ? <></>
            : <>
                <span className="key">{k}</span>
                <span className="value">{v}</span>
              </>
          }
        </Fragment>
      )
    }
    return Object.entries(obj).map(parseEntry)
  }

  const handleLogout = () => {
    user.logout()
  }

  return isShow ? (
    <SwipePanel
      onClose={close}
      onSwipe={close}
      className="main"
      header={<></>}
      height={SwipePanelHeight.FULL}
    >
      <div className="row captain" onClick={() => setPage(ProfilePage.Captain)}>
        <div className="icon"><CaptainIcon/></div>
        <div className="description">
          <span className="regular-header-text">{info?.name || t("profile.captain.no.name")}</span>
          <span className="primary-text">{info?.email || user.email}</span>
        </div>
        <div className="action"><EditIcon/></div>
      </div>
      {
        hasBoatInfo
          ? <Fragment>
              <div className="row-header">{t("profile.boat.info.header")}</div>
              <div className="row boat-info"  onClick={() => setPage(ProfilePage.Common)}>
                <div className="description">
                  <div className="header">
                    <div className="icon"><BoatCommonIcon/></div>
                    <div className="title">{`${info?.boat?.name ?? ""} ${info?.boat?.reg_number ?? ""}`}</div>
                  </div>
                  <div className="text">{constructInfo(pick(info?.boat, ["boat_type", "boat_class"]))}</div>
                </div>
                <div className="action"><RightIcon/></div>
              </div>
              <div className="row boat-info"  onClick={() => setPage(ProfilePage.Dimensions)}>
                <div className="description">
                  <div className="header">
                    <div className="icon"><BoatDimensionsIcon/></div>
                    <div className="title">{t("profile.boat.info.dimensions.header")}</div>
                  </div>
                  <div className="text">
                    {
                      constructInfo(pick(
                        info?.boat,
                        ["length", "width", "height", "draft", "fuel_rate", "cruising_speed"]
                      ))
                    }
                  </div>
                </div>
                <div className="action"><RightIcon/></div>
              </div>
              <div className="row boat-info" onClick={() => setPage(ProfilePage.Tracking)}>
                <div className="description">
                  <div className="header">
                    <div className="icon"><BoatTrackingIcon/></div>
                    <div className="title">{t("profile.boat.info.tracking.header")}</div>
                  </div>
                  <div
                    className="text">{constructInfo(pick(info?.boat, ["has_ais", "mmsi", "dest_port"]))}</div>
                </div>
                <div className="action"><RightIcon/></div>
              </div>
            </Fragment>
          : <div className="row boat-add" onClick={() => setPage(ProfilePage.Common)}>
              <div className="icon"><BoatIcon /></div>
              <div className="description">
                <span className="secondary-header-text">{t("profile.boat.add.info.title")}</span>
                <span className="list-text">{t("profile.boat.add.info.text")}</span>
              </div>
              <div className="action"><PlusIcon stroke="#0960A3"/></div>
            </div>
      }
      <div className="row-header">{t("profile.secure.header")}</div>
      <div className="row" onClick={() => setConfirmLogout(true)}>
        <div className="icon"><LogoutIcon/></div>
        <div className="description">
          <span className="title">{t("profile.logout.title")}</span>
        </div>
        <div className="action"><RightIcon/></div>
      </div>
      <ConfirmationModal
        text={t("profile.logout.confirm.text")}
        title={t("profile.logout.confirm.title")}
        buttonTitle={t("profile.logout.button.title")}
        onConfirm={handleLogout}
        onClose={() => setConfirmLogout(false)}
        show={confirmLogout}
      />
    </SwipePanel>
  ) : (<></>)
}

export default Main
